import { useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';

import { nanoid } from 'nanoid';
import { format } from 'date-fns';
import omit from 'lodash-es/omit';
import isEmpty from 'lodash-es/isEmpty';

import { GRID_SIZE, GRID_SIZE_ENL } from '@ecp/common/src/const/Paging';  // ECMJ - 박준석

import { useAlertDialog, useConfirmDialog } from '@bo-hooks/common/useModal';
import useIBSheet from '@bo-components/common/grid/useIBSheet';

const useGrid = (
  {
    data,
    title,
    options: optionsProp,
    initRow,
    total,
    unPaged,
    unLoaded,
    readOnly,
    showUploadPopup,
    handleExcelUploadClick,
    replaceDataByExcel,
    addRowValidator,
    ...props
  },
  ref
) => {
  const {
    validators,
    onSave,
    onSearch,
    onDataChange,
    onBeforeRowDelete,
    onDeleteItems,
    onCopyItems,
    onExcelDownload,
    renderSafeJobs,
    ...methodProps
  } = props;

  const sheetEl = useRef(`sheetContainer__${nanoid()}`);
  const grid = useRef(null);
  useImperativeHandle(ref, () => grid.current);

  const [isHeaderEnabled, setIsHeaderEnabled] = useState(false);
  const [checkedRows, setCheckedRows] = useState([]);

  // ECMJ - 박준석
  const gridSizesList = props.gridSizes ?? (props.sizeEnl ? GRID_SIZE_ENL : GRID_SIZE);
  const pageSize = useRef(
    gridSizesList.find(({ key }) => key === props.defaultGridSizeKey) ?? gridSizesList[0]
  );
  const pageSort = useRef('');
  const pageIndex = useRef(0);
  const cachedCheckedRows = useRef([]);
  const { showConfirmDialog } = useConfirmDialog();
  const { showAlertDialog } = useAlertDialog();

  const unPagedCfg = useMemo(() => (unPaged ? { HeaderSortMode: 1 } : {}), [unPaged]);

  useEffect(() => {
    cachedCheckedRows.current = checkedRows;
    setIsHeaderEnabled(checkedRows.length > 0);
  }, [checkedRows]);

  const clearCheck = () => {
    grid.current?.setAllCheck?.('CHK', false, true);
    setCheckedRows?.([]);
  };

  const handlePageableSearch = useCallback(
    (index, size, sort) => {
      grid.current.pageIndex = index || pageIndex.current;
      grid.current.pageSize = size || pageSize.current?.key || 120;

      onSearch?.(index || pageIndex.current, size || pageSize.current?.key || 100, sort || pageSort.current);
    },
    [onSearch]
  );

  const handleSizeChange = (item) => {
    if (pageSize.current !== item) {
      pageSize.current = item;
      pageIndex.current = 0;

      handlePageableSearch(0, item.key, pageSort.current);
    }
  };

  const handlePageChange = (pageNumber) => {
    const index = pageNumber - 1;

    if (pageIndex.current !== index) {
      pageIndex.current = index;
      handlePageableSearch(index, pageSize.current.key, pageSort.current);
    }
  };

  const handleSortChange = useCallback(
    (sort) => {
      pageSort.current = sort ? `${sort.key},${sort.order}` : '';
      handlePageableSearch(pageIndex.current, pageSize.current.key, pageSort.current);
      return true;
    },
    [handlePageableSearch]
  );

  const handleDeleteRows = () => {
    if (onBeforeRowDelete) {
      onBeforeRowDelete(deleteRows);
    } else {
      deleteRows();
    }
  };

  const deleteRows = () => {
    const { deleteRows, removeRows } = checkedRows.reduce(
      (rows, row) => {
        if (row.Added) {
          rows.removeRows.push(row);
        } else {
          rows.deleteRows.push(row);
        }
        return rows;
      },
      { deleteRows: [], removeRows: [] }
    );
    clearCheck();

    // 논리삭제
    grid.current?.deleteRows(deleteRows);

    deleteRows.map((row) => {
      row.CanEdit = 2;
      // row['CHKCanEdit'] = 1; checkbox 만 수정 가능하게
    });

    //물리 삭제
    grid.current?.removeRows(removeRows);

    onDeleteItems?.(deleteRows);
    onDataChange?.(grid.current?.hasChangedData() > 0);
  };

  const cancelDeletedRow = (deleteRow) => {
    const row = grid.current?.getRowById(deleteRow.id);
    if (row) {
      row.CanEdit = 1;
    }
    grid.current?.deleteRow(row, 0);
    grid.current?.refreshRow(row);

    onDataChange?.(grid.current?.hasChangedData() > 0);
  };

  const cancelDeletedRows = (deleteRows) => {
    const rows = deleteRows.map(({ id }) => grid.current?.getRowById(id));
    rows.map((row) => {
      row.CanEdit = 1;
    });
    grid.current?.deleteRows(rows, 0);
    grid.current?.renderBody();

    onDataChange?.(grid.current?.hasChangedData() > 0);
  };

  const handleCopyRows = async () => {
    const copiedRows = grid.current?.copyRows({
      rows: [
        ...checkedRows.map((row) => ({
          ...omit(row, ['CHK', 'rowIndex', 'createDate', 'creatorId', 'updateDate', 'updaterId']),
        })),
      ],
      next: grid.current?.getFirstRow(),
    });

    grid.current?.focus(grid.current.getFirstRow(), 'rowIndex');

    onCopyItems?.(copiedRows, checkedRows);
  };

  const handleAddRow = () => {
    clearCheck();
    if (!addRowValidator || addRowValidator(grid)) {
      grid.current?.addRow({ next: grid.current.getFirstRow(), init: { ...initRow } });
    }
  };

  const handleSave = async () => {
    clearCheck();
    const changedData = getChangedData();
    if (isEmpty(changedData)) {
      showAlertDialog('변경된 내역이 없습니다.');
      return;
    }

    const validationResult = await validate(changedData);
    if (!validationResult) {
      showAlertDialog('오류내역을 확인해주세요.');
      return;
    }

    showConfirmDialog(
      `저장하시겠습니까?\n(입력: ${changedData.created.length}건, 수정: ${changedData.updated.length}건, 삭제: ${changedData.deleted.length}건)`,
      () => onSave?.(changedData),
      () => {},
      '확인'
    );
  };

  const getChangedData = () => {
    const saveJson = grid.current?.getSaveJson({ saveMode: 3, validRequired: false });
    if (!saveJson?.data?.length) {
      return {};
    }

    return saveJson?.data?.reduce(
      (rows, row) => {
        const clearedRow = omit(row, ['CHK', 'createDate', 'creatorId', 'updateDate', 'updaterId']);
        const { STATUS: status } = clearedRow;
        if (status === 'Added') {
          rows.created.push(clearedRow);
        } else if (status === 'Deleted') {
          rows.deleted.push(clearedRow);
        } else {
          rows.updated.push(clearedRow);
        }
        return rows;
      },
      { deleted: [], updated: [], created: [] }
    );
  };

  const validate = async (data) => {
    const changedData = !data ? getChangedData() : data;
    if (isEmpty(changedData)) return true;

    const validatorKeys = Object?.keys(validators);
    const accumulator = validators?.getInitialAccumulator?.();

    const typeValidators = validatorKeys?.reduce(
      (acc, key) => {
        if (key === 'getInitialAccumulator') return acc;
        validators[key].forEach((validationItem) => {
          const { when = ['updated', 'created'] } = validationItem;
          when.forEach((type) => {
            acc[type][key] ? acc[type][key].push(validationItem) : (acc[type][key] = [validationItem]);
          });
        });

        return acc;
      },
      { deleted: {}, updated: {}, created: {} }
    );

    const validationResults = await Promise.all(
      ['updated', 'created', 'deleted'].map((type) => {
        return typeValidate(changedData[type], typeValidators[type], accumulator);
      })
    );

    const isValid = validationResults.every((valid) => valid);

    if (!isValid) {
      showAlertDialog('오류내역을 확인해주세요.');
    }

    return isValid;
  };

  const typeValidate = async (targetRows, targetValidators = {}, accumulator) => {
    if (Object.keys(targetValidators).length === 0) return true;

    const validatorKeys = Object.keys(validators);
    return targetRows.reduce(async (validRowPromise, row) => {
      const validRow = await validRowPromise;
      const targetRow = grid?.current?.getRowById?.(row.id);
      const errorDescription = [];

      const result = await validatorKeys.reduce(async (validColPromise, col) => {
        const validCol = !!(await validColPromise);
        const [resultCol, errorMessage] = await cellValidate(targetValidators[col], row, col, targetRow, accumulator);
        !resultCol && errorDescription.push(errorMessage);
        return validCol && resultCol;
      }, true);

      grid?.current?.setValue?.(targetRow, 'ErrorDescription', errorDescription.join());
      return validRow & result;
    }, true);
  };

  const cellValidate = async (targetValidators = [], row, col, targetRow, accumulator) => {
    for (const cellValidator of targetValidators) {
      const { message = '처리할 수 없는 데이터 입니다.', validator } = cellValidator;
      const isValid = await validator?.(row, accumulator);

      if (!isValid) {
        grid?.current?.setCellStyle({
          row: targetRow,
          col,
          styleAttr: { Error: true, Tip: message },
          render: 1,
        });

        return [false, message];
      } else {
        grid?.current?.getAttribute(grid.current.getRowById(row.id), col, 'Error') &&
          grid?.current?.setCellStyle({
            row: targetRow,
            col,
            styleAttr: { Error: false, Tip: '' },
            render: 1,
          });
      }
    }

    return [true];
  };

  const handleExcelTemplateDownload = () => {
    const fileName = title + '_Template.xlsx';
    const restore = clearAttributes([
      { attributeName: 'Required', initValue: 0 },
      { attributeName: 'Icon', header: true },
    ]);

    const exportOptions = {
      fileName,
      sheetDesign: 1,
      excelFontSize: 11,
      excelFontFamily: 'Malgun Gothic',
      // allTypeToText: 1,
      merge: 1,
    };

    if (isOneLineHeaderGrid()) {
      exportOptions.downRows = '0';
    }

    grid.current?.exportData(exportOptions);
    restore();
  };

  // Grid의 헤더가 한 줄인지 체크.
  const isOneLineHeaderGrid = () => {
    const cols = [...(optionsProp?.LeftCols || []), ...(optionsProp?.Cols || []), ...(optionsProp?.RightCols || [])];

    if (!cols.length) return true;

    // 헤더가 2줄인 경우, 보통 "Header: ['회원정보', '회원번호']" 이런 식으로 사용하므로,
    // Header의 length를 확인하여 한 줄인지 체크.
    return !cols.some((col) => Array.isArray(col.Header) && col.Header.length > 1);
  };

  const handleExcelDownload = () => {
    const excelDownload = (excelData = []) => {
      const fileName = title + '_' + format(new Date(), 'yyyy-MM-dd_HH:mm:ss') + '.xlsx';
      const exportOptions = {
        fileName,
        sheetDesign: 1,
        excelFontSize: 11,
        excelFontFamily: 'Malgun Gothic',
        merge: 2,
      };
      excelData?.length > 0 && (exportOptions.directExcelData = excelData);

      const restore = clearAttributes([
        { attributeName: 'TextStyle', initValue: 0 },
        { attributeName: 'Icon' },
        { attributeName: 'Icon', header: true },
        { attributeName: 'Required', initValue: 0 },
        { attributeName: 'ColMerge', initValue: false },
      ]);
      grid.current?.exportData(exportOptions);
      restore();
    };

    onExcelDownload ? onExcelDownload(excelDownload) : excelDownload();
  };

  const clearAttributes = (attributes) => {
    const restores = attributes.map(({ attributeName, initValue, header }) => {
      const row = header ? grid.current.getRowById('Header') : null;
      const clearTargets = header ? grid.current.getCols() : grid.current.getCols(attributeName);

      return restoreAttribute(
        row,
        clearTargets.map((columnName) => {
          const value = grid.current?.getAttribute(row, columnName, attributeName);
          grid.current?.setAttribute(row, columnName, attributeName, initValue, 0);
          return { columnName, value };
        }),
        attributeName
      );
    });

    return () => {
      restores.forEach((restore) => restore());
    };
  };

  const restoreAttribute =
    (row, restoreSource = [], attributeName) =>
    () => {
      restoreSource.forEach(({ columnName, value }) => {
        grid.current?.setAttribute(row, columnName, attributeName, value, 0);
      });
    };

  const handleExcelUpload = () => {
    if (showUploadPopup) {
      handleExcelUploadClick?.();
    } else if (replaceDataByExcel && grid.current?.hasChangedData() > 0 && grid.current?.getDataRows().length > 0) {
      showConfirmDialog(
        `추가한 모든데이터가 삭제됩니다.\n재업로드 진행하시겠습니까?`,
        () => grid.current?.importData({ append: !replaceDataByExcel }),
        () => {},
        '확인'
      );
    } else {
      grid.current?.importData({ append: !replaceDataByExcel });
    }
  };

  const handleExcelUploadWithMaxFileSize = () => {
    if (showUploadPopup) {
      handleExcelUploadClick?.();
    } else if (replaceDataByExcel && grid.current?.hasChangedData() > 0 && grid.current?.getDataRows().length > 0) {
      showConfirmDialog(
        `추가한 모든데이터가 삭제됩니다.\n재업로드 진행하시겠습니까?`,
        () => grid.current?.importData({ append: !replaceDataByExcel }),
        () => {},
        '확인'
      );
    } else {
      grid.current?.importData({ append: !replaceDataByExcel, endRow: 300 });
    }
  };

  const reloadData = async () => {
    await handlePageableSearch(pageIndex.current, pageSize.current.key, pageSort.current);
  };

  const getPageIndex = () => {
    return pageIndex.current;
  };

  const setPageIndex = (index) => {
    pageIndex.current = index;
  };

  const getPageSize = () => {
    return pageSize.current;
  };

  const getPageSort = () => {
    return pageSort.current;
  };

  const setPageSort = (sort) => {
    pageSort.current = sort;
  };

  const setPageSize = (size) => {
    const sizes = props.gridSizes ?? (props.sizeEnl ? GRID_SIZE_ENL : GRID_SIZE); // ECMJ - 박준석
    pageSize.current = sizes.find(({ key }) => size === key) || sizes[0]; // ECMJ - 박준석
  };

  const getCheckedRows = () => {
    return cachedCheckedRows.current;
  };

  const setFocusOut = () => {
    grid.current?.blur();
  };

  const removeAll = () => {
    grid.current?.getRows().forEach((row) => {
      grid.current?.deleteRows(row);
    })
  }

  useIBSheet(
    unPagedCfg,
    readOnly,
    optionsProp,
    onDataChange,
    handleSortChange,
    grid,
    unLoaded,
    setCheckedRows,
    handlePageableSearch,
    sheetEl,
    data,
    renderSafeJobs,
    methodProps
  );

  return {
    grid,
    sheetEl,
    unPaged,
    total,
    isHeaderEnabled,
    checkedRows,
    onSave,
    getPageSize,
    getPageIndex,
    getPageSort,
    setPageIndex,
    setPageSort,
    setPageSize,
    handleSizeChange,
    handlePageChange,
    handleDeleteRows,
    handleCopyRows,
    handleAddRow,
    handleSave,
    handleExcelTemplateDownload,
    handleExcelDownload,
    handleExcelUpload,
    handleExcelUploadWithMaxFileSize,
    validate,
    getChangedData,
    reloadData,
    getCheckedRows,
    setFocusOut,
    cancelDeletedRow,
    cancelDeletedRows,
    removeAll,
  };
};

export default useGrid;